import { ref, computed } from 'vue';
import { Client } from 'persona';

let personaClient = null; // Singleton instance outside the composable
const inquiryStatus = ref(null);
const inquiryError = ref(null);
const inquiryId = ref(null);
const isCancelled = ref(null);
const isComplete = ref(null);
const isError = computed(() => !!inquiryError.value);
const event = ref(null);

export function usePersona() {
  /**
   * Initializes the Persona client if it hasn't been created yet (Singleton)
   * @param {string} templateId - The template ID starting with itmpl_
   * @param {string} environmentId - The environment ID starting with env_
   * @param {number} referenceId - The reference ID should be the Highway User ID that comes from the refresh_jwt.
   * @see {@link https://docs.withpersona.com/docs/quickstart-embedded-flow/ Persona Embedded Flow Documentation}
   */
  const initializePersonaClient = (templateId, environmentId, referenceId) => {
    if (!personaClient) {
      isCancelled.value = null;
      isComplete.value = null;
      console.log('Initializing Persona client');
      personaClient = new Client({
        templateId,
        environmentId,
        referenceId,
        onLoad: handleLoad,
        onComplete: handleComplete,
        onCancel: handleCancel,
        onError: handleError,
        onEvent: handleEvent,
        onReady: handleReady,
      });
      return personaClient;
    }
  };
  /**
   * Initializes the Persona client for existing inquiries (Resuming)
   * @param {string} inquiryId - The inquiry ID to resume
   * @param {string} sessionToken - The session token to resume the inquiry
   * @see {@link https://docs.withpersona.com/docs/inquiries-resuming-inquiries Persona Resuming Inquiries Documentation
   */
  const initializePersonaClientWithInquiry = (inquiryId, sessionToken) => {
    if (!personaClient) {
      console.log('Initializing Persona client with inquiry id');
      personaClient = new Client({
        inquiryId,
        sessionToken,
        onLoad: handleLoad,
        onComplete: handleComplete,
        onCancel: handleCancel,
        onError: handleError,
        onEvent: handleEvent,
        onReady: handleReady,
      });
    }
    return personaClient;
  };

  const handleLoad = () => {
    console.log('Persona client loaded');
  };

  const handleComplete = ({ inquiryId, status, fields }) => {
    inquiryStatus.value = status;
    console.log(
      `Inquiry ${inquiryId} completed with status ${status} and fields ${fields}`
    );
    if (status === 'completed') {
      personaClient.destroy();
      personaClient = null;
    }
    isComplete.value = true;
  };

  const handleCancel = ({ inquiryId, sessionToken }) => {
    console.log('Inquiry was cancelled:', inquiryId, sessionToken);
    isCancelled.value = true;
    personaClient.destroy();
  };

  const handleError = (error) => {
    inquiryError.value = error;
    console.error('An error occurred:', error);
    personaClient.cancel(true);
    personaClient.destroy();
  };

  const handleReady = () => {
    if (!personaClient) {
      console.error(
        'Persona client is not initialized. Call initializePersonaClient first.'
      );
      return;
    }
    console.log('Opening Persona client', personaClient);
    personaClient.open();
  };

  const handleEvent = (eventName, metadata) => {
    console.log('Event', eventName, metadata);
    event.value = { eventName, metadata };

    if (eventName === 'start' && metadata.inquiryId) {
      inquiryId.value = metadata.inquiryId;
    }
  };

  return {
    initializePersonaClient,
    initializePersonaClientWithInquiry,
    inquiryStatus,
    inquiryError,
    inquiryId,
    handleReady,
    isComplete,
    isError,
    isCancelled,
    event,
  };
}
